import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { loadPdf, renderPageToCanvas } from "@/lib/pdfRender";

// Renders every page of a PDF to a canvas and lets the parent paint an absolutely-positioned
// overlay per page (field boxes). Coordinates are fractions of the page (0..1).
export function EsignPages({ pdfUrl, width = 720, onPageClick, onPageDrop, renderOverlay, loadingText = "Loading document…" }) {
  const [numPages, setNumPages] = useState(0);
  const [dims, setDims] = useState({});
  const [err, setErr] = useState("");
  const canvasRefs = useRef({});
  const pdfRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(pdfUrl, { responseType: "arraybuffer" });
        if (cancelled) return;
        const pdf = await loadPdf(res.data.slice(0));
        if (cancelled) return;
        pdfRef.current = pdf;
        setNumPages(pdf.numPages);
      } catch (e) {
        if (!cancelled) setErr("Could not load the document preview.");
      }
    })();
    return () => { cancelled = true; };
  }, [pdfUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pdf = pdfRef.current;
      if (!pdf || !numPages) return;
      const nd = {};
      for (let i = 1; i <= numPages; i++) {
        const canvas = canvasRefs.current[i - 1];
        if (!canvas) continue;
        try { nd[i - 1] = await renderPageToCanvas(pdf, i, canvas, width); } catch (e) { /* ignore */ }
      }
      if (!cancelled) setDims({ ...nd });
    })();
    return () => { cancelled = true; };
  }, [numPages, width]);

  if (err) return <div className="text-sm text-rose-600 p-4">{err}</div>;
  if (!numPages) return <div className="text-sm text-slate-400 p-4">{loadingText}</div>;

  const click = (e, idx) => {
    if (!onPageClick) return;
    const r = e.currentTarget.getBoundingClientRect();
    onPageClick(idx, (e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
  };
  const drop = (e, idx) => {
    if (!onPageDrop) return;
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    onPageDrop(idx, (e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height, e.dataTransfer.getData("text/plain"));
  };

  return (
    <div className="space-y-4">
      {Array.from({ length: numPages }).map((_, idx) => (
        <div key={idx} data-page={idx} className="relative mx-auto shadow-sm border border-slate-200 bg-white" style={{ width }}
          onDragOver={onPageDrop ? (e) => e.preventDefault() : undefined} onDrop={onPageDrop ? (e) => drop(e, idx) : undefined}>
          <canvas ref={(el) => { canvasRefs.current[idx] = el; }} className="block w-full" onClick={(e) => click(e, idx)} />
          <div className="absolute inset-0 pointer-events-none">{dims[idx] && renderOverlay && renderOverlay(idx, dims[idx])}</div>
        </div>
      ))}
    </div>
  );
}

export const ESIGN_COLORS = ["#1479C4", "#16a34a", "#9333ea", "#ea580c", "#0891b2", "#db2777", "#ca8a04", "#4f46e5"];
